import { useQuery } from "@tanstack/react-query";
import { orderBy } from "lodash-es";
import { useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { SB_STORAGE_KEY } from "consts";
import { Maybe, Organization, Space, SupaUser } from "types";

import * as Const from "../../../../consts";
import { supabase } from "../../../../services";
import { getProfiles } from "../../../../utils/getProfiles";
import { getSpaces } from "../../../../utils/getSpaces";
import { getPersistedSpaces, setPersistedSpaces } from "../../../../utils/persistedSpaces";
export interface UseOrganizationsAndSpaces {
  organizations: Organization[];
  currentSpaces: Space[];
  currentOrganization: Organization | null;
  changeSpaces: (spaces: Space[]) => Promise<void>;
  switchOrganization: (organization: Organization) => Promise<void>;
  isOrganizationsFetchInProgress: boolean;
}

export const useOrganizationsAndSpaces = (user: Maybe<SupaUser>): UseOrganizationsAndSpaces => {
  const navigate = useNavigate();

  const userId = user?.id;
  const currentOrgId = user?.app_metadata.profile.space_id;

  const [currentOrganization, setCurrentOrganization] = useState<Organization | null>(null);
  const [currentSpaces, setCurrentSpaces] = useState<Space[]>([]);

  const {
    data: organizations,
    isPending,
    isSuccess
  } = useQuery({
    queryKey: ["organizations", userId],
    queryFn: async () => {
      if (!userId) {
        return [];
      }

      const [profiles, spaces] = await Promise.all([getProfiles(userId), getSpaces()]);

      if (!profiles || !profiles.length) {
        localStorage.removeItem(SB_STORAGE_KEY);
        await supabase.auth.signOut();

        return [];
      }

      const isThereAnySpace = !!(spaces && spaces.length);

      return orderBy(
        profiles.map<Organization>((p) => {
          const subSpaces = isThereAnySpace
            ? spaces.filter((s) => s.parent_id === p.org_id)
            : [p.spaces];

          return {
            ...p,
            name: p.org_name,
            spaces: orderBy(subSpaces as Space[], [(space) => space.name.toLowerCase()], ["asc"])
          };
        }),
        [(profile) => profile.name.toLowerCase()],
        ["asc"]
      );
    }
  });

  const parseOrganizations = useCallback(
    async (organizations: Organization[]) => {
      if (!userId || !currentOrgId) {
        return;
      }

      const cachedSpaces = await getPersistedSpaces(userId, currentOrgId);
      const currentOrg = organizations.find((o) => o.space_id === currentOrgId);

      const cachedSpacesIds = cachedSpaces.map((s) => s.id);

      const isSelectAll = cachedSpacesIds.includes(Const.WORKSPACE_SELECTION_SELECT_ALL_KEY);

      const filteredSpaces = currentOrg?.spaces.filter((s) => cachedSpacesIds.includes(s.id)) || [];

      const nextSpaces =
        isSelectAll || !filteredSpaces.length
          ? Const.WORKSPACE_SELECTION_SELECT_ALL
          : filteredSpaces;

      setCurrentSpaces(nextSpaces);
      setCurrentOrganization(currentOrg ?? organizations[0]);

      await setPersistedSpaces(nextSpaces, userId, currentOrgId);
    },
    [currentOrgId, userId]
  );

  const switchOrganization = useCallback(
    async (organization: Organization) => {
      try {
        await supabase.rpc("switch_profile", { profile_id: organization.id });
        await supabase.auth.refreshSession();

        navigate("/");

        window.location.reload();
      } catch (e) {
        console.log(e);
      }
    },
    [navigate]
  );

  const changeSpaces = useCallback(
    async (spaces: Space[]) => {
      setCurrentSpaces(spaces);

      await setPersistedSpaces(spaces, userId, currentOrganization?.space_id);
    },
    [currentOrganization?.space_id, userId]
  );

  useEffect(() => {
    if (isSuccess && organizations) {
      parseOrganizations(organizations);
    }
  }, [isSuccess, organizations, parseOrganizations]);

  return {
    organizations: organizations ?? [],
    currentSpaces,
    currentOrganization,
    changeSpaces,
    switchOrganization,
    isOrganizationsFetchInProgress: (isPending || !currentOrganization) && !!user
  };
};
